"use client";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { IResource, IUserSelectable } from "@gettactic/api";
import { UserSelectInput } from "@gettactic/components/src/form/UserSelectInput/UserSelectInput";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { useDisclosure } from "@gettactic/helpers";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import { isDedicatedFull } from "@gettactic/helpers/src/resources/dedicatedSeats";
import {
  useAuthenticated,
  useFloors,
  useUserListByIds,
  useUserListPaginated
} from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { MapPin, X } from "lucide-react";
import React from "react";
import { useResourceDedicatedUsers } from "@/features/map/hooks/useResourceDedicatedUsers";
import { api } from "@/lib/api/api";
import {
  apiErrorCode,
  dedicatedUserErrorMessage,
  isMoveConflict
} from "@/lib/dedicated-user-errors";
import { useDebouncedValue } from "@/lib/hooks/useDebouncedValue";
import { DedicatedExceptionsSection } from "./DedicatedExceptionsSection";
import { dedicatedOwnersText } from "./dedicatedOwnersText";

/** Page size for the dedicated-user picker's server-side search. */
const USER_PAGE_SIZE = 20;
const USER_SEARCH_DEBOUNCE_MS = 300;

// Lazy-loaded to break the module cycle: the embedded map pulls in the map
// view, which renders the resource card, which renders the drawer that
// hosts this assignments tab.
const MapViewEmbeddedLazy = React.lazy(() =>
  import("@/features/map/components/MapView/MapViewEmbedded").then((m) => ({
    default: m.MapViewEmbedded
  }))
);

/** 409 body of POST /resources/:id/dedicated_users when the user already
 *  has a dedicated resource — details carry that resource. */
interface DedicatedConflictBody {
  error?: {
    code?: string;
    details?: Partial<IResource> | null;
  };
}

export interface DedicatedOwnersSectionProps {
  resource: IResource;
  onChanged: () => void;
}

/**
 * Management UI for a dedicated resource's owners: who it belongs to, with
 * add (POST, retried with confirm_move after a conflict) and remove.
 */
export const DedicatedOwnersSection: React.FC<DedicatedOwnersSectionProps> = ({
  resource,
  onChanged
}) => {
  const queryClient = useQueryClient();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [userQuery, setUserQuery] = React.useState("");
  const [debouncedUserQuery] = useDebouncedValue(
    userQuery,
    USER_SEARCH_DEBOUNCE_MS
  );
  const userList = useUserListPaginated(debouncedUserQuery, USER_PAGE_SIZE);
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const moveConfirmDisclosure = useDisclosure<{
    userId: string;
    /** Where the user is currently sitting (409 details), when known. */
    currentResource: Partial<IResource> | null;
  }>();
  const [isConflictMapOpen, setIsConflictMapOpen] = React.useState(false);
  // Overlay target for the conflict map's minimap/zoom controls, so they
  // position inside the modal's map box instead of the viewport.
  const [conflictMapControls, setConflictMapControls] =
    React.useState<HTMLDivElement | null>(null);
  const loadingToast = useLoadingToast({
    loadingMessage: "Applying changes..."
  });

  // Resolve the conflicting desk's floor/office names for the move modal.
  const conflictResource = moveConfirmDisclosure.data?.currentResource;
  const { floors: conflictFloors } = useFloors(
    conflictResource?.office_id ?? null
  );
  const conflictFloorName = conflictResource?.parent_id
    ? conflictFloors?.find((f) => f.id === conflictResource.parent_id)?.name
    : undefined;
  const conflictOfficeName = conflictResource?.office_id
    ? authenticatedUser?.offices?.byId?.[conflictResource.office_id]?.name
    : undefined;

  const dedicatedUsers = useResourceDedicatedUsers(resource.id);

  const owners = dedicatedUsers.data ?? [];
  const ownerIds = new Set(owners.map((owner) => owner.id));
  const isFull = isDedicatedFull(resource, owners.length);
  const text = dedicatedOwnersText(resource, owners.length);
  const ownerIdList = React.useMemo(
    () => owners.map((owner) => owner.id),
    [owners]
  );
  const officeTimeZone =
    authenticatedUser?.offices?.byId?.[resource.office_id]?.time_zone ||
    getDeviceTZ() ||
    "UTC";

  // The users we must render are not guaranteed to be on the current search page, so fetch them by id
  const hydratedIdsKey = [
    ...new Set(
      [
        ...owners.map((owner) => owner.id),
        selectedUserId,
        moveConfirmDisclosure.data?.userId
      ].filter(Boolean)
    )
  ].join(",");
  const hydratedIds = React.useMemo(
    () => (hydratedIdsKey ? hydratedIdsKey.split(",") : []),
    [hydratedIdsKey]
  );
  const hydratedUsers = useUserListByIds(hydratedIds);
  // Cache of just the pinned (dedicated/selected/conflict) users' data —
  // scoped to `hydratedIds` and pruned to it every render. This is NOT a
  // general users cache: `userList.allPages` must stay exactly the current
  // search page for the dropdown to reflect real search results, but a
  // pinned id can briefly resolve to nothing while its hydration query is
  // re-fetching (e.g. right after picking a new user), so its last-known
  // data is kept until the fresh fetch lands.
  const pinnedUsersCacheRef = React.useRef<Map<string, IUserSelectable>>(
    new Map()
  );
  const knownUsers = React.useMemo(() => {
    const cache = pinnedUsersCacheRef.current;
    const pinnedIds = new Set(hydratedIds);
    for (const id of [...cache.keys()]) {
      if (!pinnedIds.has(id)) cache.delete(id);
    }
    for (const user of (hydratedUsers.data ?? []).filter(
      (user) => user !== null
    )) {
      cache.set(user.id, user);
    }
    for (const user of userList.allPages) {
      if (pinnedIds.has(user.id)) cache.set(user.id, user);
    }

    const byId = new Map<string, IUserSelectable>();
    for (const user of cache.values()) byId.set(user.id, user);
    for (const user of userList.allPages) byId.set(user.id, user);
    return [...byId.values()];
  }, [hydratedUsers.data, userList.allPages, hydratedIds]);

  const conflictUserName = knownUsers.find(
    (u) => u.id === moveConfirmDisclosure.data?.userId
  )?.name;

  // The dedicated-users payload may carry ids only — resolve display
  // names from the users we have loaded.
  const resolveUserName = (user: { id: string; name: string }) =>
    user.name || knownUsers.find((u) => u.id === user.id)?.name || user.id;

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["resources.dedicatedUsers", resource.id]
      }),
      queryClient.invalidateQueries({
        queryKey: ["organizations.resourcesAll"]
      })
    ]);
    onChanged();
  };

  const addUser = async (userId: string, confirmMove: boolean) => {
    setIsSaving(true);
    loadingToast.startLoading();
    const response = await api.client.organizations.resourceDedicatedUserPost(
      resource.id,
      { user_id: userId, confirm_move: confirmMove }
    );
    setIsSaving(false);

    if (response.ok) {
      loadingToast.success("Resource dedicated to the user");
      setSelectedUserId("");
      setUserQuery("");
      moveConfirmDisclosure.close();
      await refresh();
      return;
    }
    // Conflict: the user already has another dedicated resource — show
    // where they're currently sitting (409 details) and ask before moving
    // them here (retries with confirm_move: true).
    if (!confirmMove && isMoveConflict(response)) {
      loadingToast.dismiss();
      const body = response.result as DedicatedConflictBody | null;
      setIsConflictMapOpen(false);
      moveConfirmDisclosure.open({
        userId,
        currentResource: body?.error?.details ?? null
      });
      return;
    }
    loadingToast.error(
      dedicatedUserErrorMessage(apiErrorCode(response.result))
    );
  };

  const removeUser = async (userId: string) => {
    setIsSaving(true);
    loadingToast.startLoading();
    const response = await api.client.organizations.resourceDedicatedUserDelete(
      resource.id,
      userId
    );
    setIsSaving(false);
    if (response.ok) {
      loadingToast.success("Dedicated user removed");
      await refresh();
    } else {
      loadingToast.error("Could not remove the dedicated user");
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
      {dedicatedUsers.isLoading ? (
        <p className="text-neutral-400 text-xs">Loading dedicated user…</p>
      ) : (
        <>
          {text.seatCounter ? (
            <p className="font-medium text-neutral-500 text-xs">
              Owners · {text.seatCounter}
            </p>
          ) : null}
          {owners.map((owner) => (
            <React.Fragment key={owner.id}>
              <div className="flex items-center justify-between gap-2.5 rounded-md border border-neutral-200 bg-white px-2.5 py-2">
                <UserAvatar
                  user={
                    knownUsers.find((u) => u.id === owner.id) ?? {
                      id: owner.id,
                      name: owner.name
                    }
                  }
                  size="sm"
                  className="flex-none"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-neutral-800 text-sm">
                    {resolveUserName(owner)}
                  </p>
                  <p className="text-[11px] text-neutral-400">
                    {text.ownerCaption}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => removeUser(owner.id)}
                  className="flex-none rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                  title="Remove dedicated user"
                >
                  <X size={13} />
                </button>
              </div>
              <DedicatedExceptionsSection
                resource={resource}
                ownerId={owner.id}
                ownerName={resolveUserName(owner)}
                ownerIds={ownerIdList}
                timeZone={officeTimeZone}
                onChanged={onChanged}
              />
            </React.Fragment>
          ))}
          {isFull ? (
            text.fullHint ? (
              <p className="text-neutral-500 text-xs">{text.fullHint}</p>
            ) : null
          ) : (
            <>
              <p className="text-neutral-500 text-xs">{text.pickerPrompt}</p>
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <UserSelectInput
                    users={knownUsers.filter((user) => !ownerIds.has(user.id))}
                    value={selectedUserId}
                    onChangeValue={setSelectedUserId}
                    placeholder="Select a user…"
                    onSearchChange={setUserQuery}
                    isSearching={
                      userQuery !== debouncedUserQuery ||
                      (userList.isFetching && !userList.isFetchingNextPage)
                    }
                    hasNextPage={userList.hasNextPage}
                    fetchNextPage={userList.fetchNextPage}
                    isFetchingNextPage={userList.isFetchingNextPage}
                  />
                </div>
                <button
                  type="button"
                  disabled={!selectedUserId || isSaving}
                  onClick={() => addUser(selectedUserId, false)}
                  className="h-9 flex-none rounded-md bg-primary px-3 font-medium text-sm text-white hover:opacity-90 disabled:opacity-50"
                >
                  Assign
                </button>
              </div>
            </>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={moveConfirmDisclosure.isOpen}
        onOpenChange={moveConfirmDisclosure.setOpen}
        onConfirm={() => {
          const userId = moveConfirmDisclosure.data?.userId;
          if (userId) addUser(userId, true);
        }}
        title="Move dedicated desk?"
        confirmLabel="Move here"
        message={
          <div className="flex flex-col gap-2">
            <p>
              {conflictUserName || "This user"} already has a dedicated desk
              {conflictResource ? ":" : "."}
            </p>
            {conflictResource && (
              <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-800 text-sm">
                      {conflictResource.name}
                      {conflictResource.label ? (
                        <span className="ml-2 rounded-full bg-neutral-200 px-2 py-0.5 font-normal text-[11px] text-neutral-600">
                          {conflictResource.label}
                        </span>
                      ) : null}
                    </p>
                    {(conflictFloorName || conflictOfficeName) && (
                      <p className="mt-0.5 text-neutral-500 text-xs">
                        {[conflictFloorName, conflictOfficeName]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                  {conflictResource.id &&
                    conflictResource.office_id &&
                    conflictResource.parent_id && (
                      <button
                        type="button"
                        onClick={() => setIsConflictMapOpen((v) => !v)}
                        className="inline-flex flex-none items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 font-medium text-neutral-600 text-xs hover:bg-neutral-100"
                      >
                        <MapPin size={12} />
                        {isConflictMapOpen ? "Hide map" : "Show on map"}
                      </button>
                    )}
                </div>
                {isConflictMapOpen &&
                  conflictResource.id &&
                  conflictResource.office_id &&
                  conflictResource.parent_id && (
                    <div className="relative mt-2 h-[220px] overflow-hidden rounded-md border border-neutral-200 bg-white">
                      <React.Suspense
                        fallback={
                          <div className="flex h-full items-center justify-center text-neutral-400 text-xs">
                            Loading map…
                          </div>
                        }
                      >
                        <MapViewEmbeddedLazy
                          officeId={conflictResource.office_id}
                          floorId={conflictResource.parent_id}
                          focussedResourceId={conflictResource.id}
                          enableControls={false}
                          controlsPortalTarget={conflictMapControls}
                        />
                      </React.Suspense>
                      <div
                        ref={setConflictMapControls}
                        className="pointer-events-none absolute inset-0 z-10"
                      />
                    </div>
                  )}
              </div>
            )}
            <p>
              Moving them here removes that dedication. Do you want to continue?
            </p>
          </div>
        }
      />
    </div>
  );
};
